import { useEffect, useRef, useState } from "react";
import {
  formatDateOnly,
  formatMoney,
  formatPublicEventDate,
  isChapterOrganization,
  isLibertyLongRange,
  workspaceNoun
} from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";
import { memberPaymentStatusLabel } from "./MemberPortalPaymentsPanel";
import {
  RELEASE_FORM_ACTION_LABEL,
  backgroundCheckStatusCopy,
  memberReviewFollowUpCopy,
  memberStatusLabel,
  qualificationDisplayLabel,
  waiverDocumentName,
  waiverIsRequired,
  waiverRequiresSignature,
  waiverStatusCopy
} from "./memberPortalUtils";
import {
  applicationAnswerDisplay,
  applicationFormIsPublished,
  applicationFormSectionsWithResponses,
  normalizeApplicationFormForClient
} from "../membership/applicationForm";

function planInitialCheckoutPricing(plan = {}) {
  const paymentPolicy = plan.paymentPolicy || {};
  if (paymentPolicy.mode === "prescreen_then_balance") {
    return {
      amountCents: paymentPolicy.prescreeningFeeCents || 0,
      currency: plan.pricing?.currency || "usd"
    };
  }
  return plan.pricing || {};
}

function planRequiresCheckout(plan = {}) {
  return Number.parseInt(planInitialCheckoutPricing(plan).amountCents || 0, 10) > 0;
}

function planCheckoutReady(plan = {}) {
  if (!planRequiresCheckout(plan)) {
    return true;
  }
  return plan.payment?.onlineCheckoutReady !== false;
}

function planCanStartWithoutHostedCheckout(plan = {}) {
  if (!planRequiresCheckout(plan) || planCheckoutReady(plan)) {
    return false;
  }
  return /not connected|not configured/i.test(plan.payment?.label || "");
}

function planCheckoutLabel(plan = {}) {
  if (!planRequiresCheckout(plan)) {
    return "";
  }
  return plan.payment?.label || "Secure online checkout";
}

function planUnavailableMessage(plan = {}) {
  if (plan.availability?.soldOut) {
    return "Sold out";
  }
  if (planRequiresCheckout(plan) && !planCheckoutReady(plan) && !planCanStartWithoutHostedCheckout(plan)) {
    return plan.payment?.label || "Online checkout is not ready yet";
  }
  return "";
}

function registrationLooksLikeOrientation(registration = {}) {
  const event = registration.event || {};
  const text = [event.type, event.title, event.description, event.registrationPolicy?.instructions]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return text.includes("orientation") || text.includes("range tour") || text.includes("safety briefing");
}

function orientationAttendanceCopy(prequalification = {}, eventRegistrations = [], range = {}) {
  const orientationRegistrations = eventRegistrations
    .filter(registrationLooksLikeOrientation)
    .sort((left, right) => Date.parse(right.event?.startsAt || right.updatedAt || 0) - Date.parse(left.event?.startsAt || left.updatedAt || 0));
  const attended = orientationRegistrations.find((registration) => registration.attendance?.status === "attended" || registration.status === "attended");
  if (attended) {
    return {
      label: "Attended",
      detail: attended.attendance?.checkedInAt
        ? `Recorded ${formatDateOnly(attended.attendance.checkedInAt)}.`
        : `Recorded for ${attended.event?.title || "orientation"}.`,
      registration: attended
    };
  }
  const scheduled = orientationRegistrations.find((registration) => (
    !["cancelled", "no_show"].includes(registration.status) &&
    Date.parse(registration.event?.startsAt || 0) >= Date.now() - 3600000
  ));
  if (scheduled) {
    return {
      label: "Scheduled",
      detail: `${scheduled.event?.title || "Orientation"} / ${formatPublicEventDate(scheduled.event?.startsAt)}`,
      registration: scheduled
    };
  }
  const noShow = orientationRegistrations.find((registration) => registration.attendance?.status === "no_show" || registration.status === "no_show");
  if (noShow) {
    return {
      label: "Needs reschedule",
      detail: "A prior orientation was marked no-show. Register for another orientation or contact the range.",
      registration: noShow
    };
  }
  const excused = orientationRegistrations.find((registration) => registration.attendance?.status === "excused");
  if (excused) {
    return {
      label: "Excused",
      detail: "The range excused the prior orientation record. Contact the range for next steps.",
      registration: excused
    };
  }
  const orientationCopy = orientationStatusCopy(prequalification, range);
  return {
    label: orientationCopy.label,
    detail: orientationCopy.detail,
    registration: null
  };
}

function memberSubscriptionSummary(member = {}) {
  const plan = member.membershipPlan || {};
  const stripe = member.stripe || {};
  const billing = plan.billing || {};
  const status = stripe.subscriptionStatus || stripe.paymentStatus || "";
  const recurring = billing.model === "subscription" || stripe.mode === "subscription" || Boolean(status);
  const recurringTerm = !recurring &&
    billing.model !== "one_time" &&
    plan.category !== "day_pass" &&
    !plan.lifetime &&
    plan.term?.unit &&
    plan.term.unit !== "lifetime" &&
    Number(plan.pricing?.amountCents || 0) > 0;
  const cadence = recurring
    ? `Every ${billing.intervalCount && Number(billing.intervalCount) > 1 ? `${billing.intervalCount} ` : ""}${billing.interval || plan.term?.unit || "year"}${billing.intervalCount && Number(billing.intervalCount) > 1 ? "s" : ""}`
    : recurringTerm
      ? `Every ${plan.term?.count && Number(plan.term.count) > 1 ? `${plan.term.count} ` : ""}${plan.term.unit}${plan.term.count && Number(plan.term.count) > 1 ? "s" : ""}`
    : plan.term?.unit === "lifetime" || plan.lifetime
      ? "Lifetime"
      : "One-time";
  const nextBillingDate = stripe.currentPeriodEnd || member.membership?.renewalDate || member.membership?.currentTermEnd;
  return {
    recurring,
    statusLabel: status ? memberPaymentStatusLabel(status) : recurring ? "Subscription" : plan.pricing?.amountCents ? "Dues recorded" : "No balance due",
    cadence,
    nextBillingLabel: nextBillingDate
      ? `${stripe.cancelAtPeriodEnd ? "Access through" : recurring ? "Next billing" : "Term ends"} ${formatDateOnly(nextBillingDate)}`
      : plan.term?.unit === "lifetime" || plan.lifetime
        ? "No next billing"
        : "Next billing not recorded",
    cancelLabel: stripe.cancelAtPeriodEnd ? "Cancels at period end" : ""
  };
}

function formatTerm(plan) {
  if (plan.category === "day_pass") {
    const count = plan.term?.count || 1;
    return count === 1 ? "Day pass" : `${count}-day pass`;
  }
  if (plan.lifetime || plan.term?.unit === "lifetime") {
    return "Lifetime membership";
  }
  const count = plan.term?.count || 1;
  const unit = plan.term?.unit || "year";
  const suffix = count === 1 ? unit : `${unit}s`;
  return `${count} ${suffix}`;
}

function memberPortalFriendlyError(error = "", workspace = "range") {
  const message = String(error || "").trim();
  if (!message) {
    return "";
  }
  if (/authorization header|required|unauthorized|forbidden|jwt|token/i.test(message)) {
    return `We could not refresh your membership details for this ${workspace}. Sign out and back in if this does not clear.`;
  }
  return message;
}

function formatBilling(plan) {
  if (plan.category === "day_pass") {
    return "One-time day pass";
  }
  if (plan.billing?.model === "one_time") {
    return "One-time payment";
  }
  return `Subscription / ${plan.billing?.interval || plan.term?.unit || "year"}`;
}

function formatMemberAccessLabel(member = {}) {
  if (member.status === "active") {
    return "Can use member access";
  }
  if (member.status === "expired") {
    return "Expired until renewed";
  }
  if (member.status === "suspended") {
    return "Suspended by range";
  }
  return "Waiting for range approval";
}

function formatReservationAccessLabel(member = {}) {
  const laneReservation = member.membershipPlan?.accessRules?.laneReservation || {};
  if (member.status !== "active") {
    return "Available after activation";
  }
  if (laneReservation.allowed) {
    return "Included for this plan";
  }
  return "Not included in this plan";
}

function memberPlanBenefitItems(member = {}) {
  const plan = member.membershipPlan || {};
  const items = [];
  const laneReservation = plan.accessRules?.laneReservation || {};
  const guestPass = plan.guestPassPolicy || {};
  const gunRental = plan.accessRules?.gunRental || {};

  if (Array.isArray(plan.benefits?.items)) {
    plan.benefits.items
      .map((item) => (typeof item === "string" ? item : item?.label || item?.name || ""))
      .filter(Boolean)
      .slice(0, 6)
      .forEach((item) => items.push(item));
  }
  if (plan.benefits?.notes) {
    items.push(plan.benefits.notes);
  }
  if (laneReservation.allowed) {
    const pieces = ["Reservation access"];
    if (Number(laneReservation.advanceBookingDays) > 0) {
      pieces.push(`${laneReservation.advanceBookingDays} days ahead`);
    }
    if (Number(laneReservation.maxActiveReservations) > 0) {
      pieces.push(`${laneReservation.maxActiveReservations} active bookings`);
    }
    items.push(pieces.join(" / "));
  }
  if (guestPass.enabled && Number(guestPass.passes) > 0) {
    items.push(`${guestPass.passes} guest pass${Number(guestPass.passes) === 1 ? "" : "es"} per ${guestPass.period || "term"}`);
  }
  if (gunRental.allowed) {
    items.push(Number(gunRental.discountPercent) > 0
      ? `${gunRental.discountPercent}% rental discount`
      : "Rental benefit included");
  }

  return [...new Set(items.filter(Boolean))].slice(0, 8);
}

function memberChapterLabel(member = {}) {
  const chapter = member.chapterSnapshot || {};
  const location = [chapter.city, chapter.region || chapter.state].filter(Boolean).join(", ");
  return [chapter.name, location].filter(Boolean).join(" / ");
}

const LIBERTY_LONG_RANGE_QUALIFICATION_KEY = "liberty_1000_yard_qualified";

function isLongRangeQualificationKey(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("liberty_1000_yard_qualified") ||
    normalized.includes("1000") ||
    normalized.includes("1,000") ||
    normalized.includes("long range") ||
    normalized.includes("long-range")
  );
}

function canonicalQualificationKey(value = "") {
  const cleaned = String(value || "").trim();
  if (!cleaned) {
    return "";
  }
  return isLongRangeQualificationKey(cleaned) ? LIBERTY_LONG_RANGE_QUALIFICATION_KEY : cleaned;
}

function qualificationStatusLabel(status = "") {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") {
    return "Active";
  }
  if (normalized === "pending") {
    return "Waiting for range review";
  }
  if (normalized === "expired") {
    return "Expired";
  }
  if (normalized === "revoked") {
    return "Removed";
  }
  return normalized ? qualificationDisplayLabel(normalized) : "Not started";
}

function qualificationDateLabel(qualification = {}) {
  if (qualification.status === "active") {
    return qualification.expiresAt
      ? `Expires ${formatDateOnly(qualification.expiresAt)}`
      : "No expiration on file";
  }
  if (qualification.status === "pending") {
    return qualification.requestedAt
      ? `Requested ${formatDateOnly(qualification.requestedAt)}`
      : "Requested";
  }
  if (qualification.expiresAt) {
    return `Expired ${formatDateOnly(qualification.expiresAt)}`;
  }
  return qualification.updatedAt ? `Updated ${formatDateOnly(qualification.updatedAt)}` : "No date on file";
}

function missingQualificationSummaries(reservationEligibility = {}) {
  const byKey = new Map();
  const resources = reservationEligibility?.resources || [];
  resources.forEach((resource) => {
    const access = resource.qualificationAccess || {};
    (access.missingQualificationKeys || []).forEach((key) => {
      const normalizedKey = String(key || "").trim();
      if (!normalizedKey) {
        return;
      }
      const displayKey = canonicalQualificationKey(normalizedKey);
      const existing = byKey.get(displayKey) || {
        key: normalizedKey,
        displayKey,
        label: qualificationDisplayLabel(normalizedKey),
        resources: []
      };
      existing.resources.push(resource.name || resource.label || resource.resourceId || "Restricted resource");
      byKey.set(displayKey, existing);
    });
  });
  return [...byKey.values()].sort((left, right) => left.label.localeCompare(right.label));
}

function requirementStatusLabel(requirement = {}) {
  if (!requirement.required) {
    return "Not required";
  }
  if (requirement.status === "completed") {
    return "Complete";
  }
  return "Pending";
}

function checklistIncludes(checklist = [], patterns = []) {
  return checklist.some((step) => {
    const text = [step.key, step.label, step.description, step.instructions]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return patterns.some((pattern) => text.includes(pattern));
  });
}

function orientationStatusCopy(prequalification = {}, range = {}) {
  const checklist = prequalification.checklist || [];
  const hasOrientationStep = checklistIncludes(checklist, ["orientation", "range tour", "rules"]);
  if (prequalification.required === false || !prequalification.required) {
    return isLibertyLongRange(range)
      ? {
          label: "Range will schedule",
          detail: "Orientation is expected for Liberty members; scheduling support is not enabled yet."
        }
      : {
          label: "Not required",
          detail: "No orientation requirement is active for this membership."
        };
  }
  if (prequalification.status === "completed") {
    return {
      label: "Complete",
      detail: hasOrientationStep
        ? "The range has your onboarding/orientation responses on file."
        : "The range has your onboarding responses on file."
    };
  }
  if (hasOrientationStep || isLibertyLongRange(range)) {
    return {
      label: "Pending",
      detail: "Complete the orientation checklist or wait for the range to schedule an orientation session."
    };
  }
  return {
    label: requirementStatusLabel(prequalification),
    detail: prequalification.instructions || "Complete the member onboarding checklist for range review."
  };
}

function longRangeGateStatusCopy(member = {}, range = {}) {
  const qualifications = member?.qualifications?.items || [];
  const gate = qualifications.find((qualification) => {
    return isLongRangeQualificationKey(qualification.key) || isLongRangeQualificationKey(qualification.label);
  });

  if (gate?.status === "active") {
    return {
      label: "Qualified",
      detail: gate.expiresAt
        ? `Valid until ${formatDateOnly(gate.expiresAt)}`
        : "The 1,000-yard qualification is active."
    };
  }
  if (gate?.status === "pending") {
    return {
      label: "Range review",
      detail: "Your 1,000-yard qualification request is waiting for range review."
    };
  }
  if (gate?.status === "expired") {
    return {
      label: "Expired",
      detail: "Schedule another 1,000-yard qualification before using the gated long-range area."
    };
  }
  if (isLibertyLongRange(range)) {
    return {
      label: "Not qualified yet",
      detail: "The 1,000-yard area requires a separate range-approved qualification."
    };
  }
  return {
    label: "Not required",
    detail: "No separate long-range gate is configured for this member record."
  };
}

function accessCredentialCopy(member = {}) {
  const credential = member.accessCredential || {};
  const labels = {
    not_issued: "Not issued",
    ready_to_issue: "Ready to issue",
    issued: "Issued",
    active: "Active",
    disabled: "Disabled",
    lost: "Lost",
    returned: "Returned"
  };
  const status = credential.status || "not_issued";
  if (status === "active") {
    return {
      label: "Active",
      detail: credential.cardLast4 ? `Access card ending ${credential.cardLast4}` : "Your range access credential is active."
    };
  }
  if (status === "ready_to_issue") {
    return {
      label: "Ready to issue",
      detail: "Staff can issue your RFID/access card when you arrive."
    };
  }
  if (status === "issued") {
    return {
      label: "Issued",
      detail: credential.cardLast4 ? `Issued card ending ${credential.cardLast4}; activation may still be pending.` : "Issued; activation may still be pending."
    };
  }
  if (["disabled", "lost", "returned"].includes(status)) {
    return {
      label: labels[status],
      detail: "Contact the range before using card-controlled areas."
    };
  }
  return {
    label: labels[status] || "Not issued",
    detail: "Staff will issue an access card if your membership and gates require it."
  };
}

function libertyAccessMilestones({ member = {}, prequalification = {}, range = {}, eventRegistrations = [] } = {}) {
  if (!isLibertyLongRange(range)) {
    return [];
  }
  const checklist = prequalification.checklist || [];
  const hasOrientationStep = checklistIncludes(checklist, ["orientation", "range tour", "rules"]);
  const hasLongRangeStep = checklistIncludes(checklist, ["1000", "1,000", "long range", "long-range"]);
  const orientationCopy = orientationAttendanceCopy(prequalification, eventRegistrations, range);
  const longRangeCopy = longRangeGateStatusCopy(member, range);
  return [
    {
      key: "orientation",
      title: "Range orientation",
      label: orientationCopy.label,
      body: hasOrientationStep
        ? "Configured as part of the onboarding checklist: range tour, safety rules, and range commands."
        : "Range-managed step: staff should schedule or record the safety orientation before full access.",
      detail: orientationCopy.detail,
      required: true
    },
    {
      key: "long_range_qualification",
      title: "Optional 1,000-yard qualification",
      label: longRangeCopy.label,
      body: hasLongRangeStep
        ? "Configured in onboarding so members can request long-range qualification during the signup path."
        : "Separate gate: members can qualify during orientation or request a later qualification review.",
      detail: longRangeCopy.detail,
      required: false
    }
  ];
}

function onboardingStepPrompt(step = {}) {
  if (step.instructions || step.description) {
    return step.instructions || step.description;
  }
  const label = String(step.label || "").toLowerCase();
  if (label.includes("orientation") || label.includes("range tour") || label.includes("rules")) {
    return "Confirm your availability or note anything the range should know before scheduling.";
  }
  if (label.includes("1000") || label.includes("1,000") || label.includes("long range")) {
    return "Tell the range whether you want to qualify for the gated long-range area.";
  }
  if (label.includes("background")) {
    return "Provide any range-requested context for this review step.";
  }
  return "Add a short response so the range can review this step.";
}

function onboardingStepStatusCopy(step = {}, canSubmit = false) {
  if (step.status === "completed") {
    return {
      label: "Complete",
      detail: step.completedAt
        ? `Completed ${formatDateOnly(step.completedAt)}.`
        : "The range marked this step complete."
    };
  }
  if (step.applicantSubmittedAt || step.applicantResponse) {
    return {
      label: "Submitted",
      detail: step.applicantSubmittedAt
        ? `Submitted ${formatDateOnly(step.applicantSubmittedAt)}. Waiting for range review.`
        : "Response saved. Waiting for range review."
    };
  }
  if (canSubmit) {
    return {
      label: "Needs response",
      detail: "Add your response below."
    };
  }
  return {
    label: step.required === false ? "Optional" : "Pending",
    detail: step.required === false
      ? "This step is optional unless the range asks for it."
      : "The range still needs to complete or review this step."
  };
}

function onboardingChecklistSummary(checklist = []) {
  const total = checklist.length;
  const complete = checklist.filter((step) => step.status === "completed").length;
  const submitted = checklist.filter((step) => step.status !== "completed" && (step.applicantSubmittedAt || step.applicantResponse)).length;
  const open = Math.max(total - complete - submitted, 0);
  return { complete, submitted, open, total };
}

function waiverExpirationRule(waiver = {}) {
  const expiresInDays = Number.parseInt(waiver.expiresInDays || 0, 10);
  if (!Number.isFinite(expiresInDays) || expiresInDays <= 0) {
    return "No automatic re-sign schedule";
  }
  if (expiresInDays === 365) {
    return "Re-sign every year";
  }
  if (expiresInDays % 365 === 0) {
    return `Re-sign every ${expiresInDays / 365} years`;
  }
  if (expiresInDays % 30 === 0) {
    return `Re-sign every ${expiresInDays / 30} months`;
  }
  return `Re-sign every ${expiresInDays} days`;
}

function waiverSignatureLabel(waiver = {}) {
  return waiverStatusCopy(waiver).signature;
}

function currentStatusCopy(statusContext) {
  if (!statusContext || statusContext.current?.kind === "none") {
    return {
      title: "No membership signup found",
      body: "Start a membership signup below to see your progress here."
    };
  }

  if (statusContext.current?.kind === "signup_intent") {
    const paymentStatus = statusContext.current.paymentStatus;
    if (paymentStatus === "checkout_started") {
      return {
        title: "Checkout started",
        body: "Finish secure checkout to send your membership signup to the range."
      };
    }
    if (paymentStatus === "payment_pending") {
      const workspace = workspaceNoun(statusContext.range, "organization");
      return {
        title: "Membership request received",
        body: `Your request is in. The ${workspace} team will review it and follow up with payment instructions.`
      };
    }
    if (paymentStatus === "paid" || paymentStatus === "not_required") {
      return {
        title: "Payment received",
        body: "The range is preparing your member record. Refresh if this was just completed."
      };
    }
    return {
      title: "Signup started",
      body: "Your membership signup has started, but payment or activation is not complete yet."
    };
  }

  const member = statusContext.members?.find((item) => item.memberId === statusContext.current.memberId);
  const followUp = memberReviewFollowUpCopy(member);
  if (followUp) {
    return {
      title: followUp.title,
      body: followUp.body
    };
  }
  const isDayPass = member?.membershipPlan?.category === "day_pass";
  if (member?.status === "active") {
    return {
      title: isDayPass ? "Day pass active" : "Membership active",
      body: isDayPass
        ? "Your day pass is active. Use your ArmorySync account when you arrive at the range."
        : "Your member record is active. Use your ArmorySync account for member access."
    };
  }
  if (member?.reviewSummary?.requirementsComplete) {
    return {
      title: "Ready for range review",
      body: "Your required waiver and qualification steps are complete. The range team can now finish approval."
    };
  }
  return {
    title: "Membership in review",
    body: "The range team is reviewing your waiver, onboarding, background-check status, and member record."
  };
}

export function ApplicantStatusPanel({
  id,
  acknowledgingWaiver,
  acknowledgingGuestWaiverEntryId,
  loading,
  prequalificationResponses,
  redeemingGuestPass,
  statusContext,
  submittingPrequalification,
  error,
  onAcknowledgeWaiver,
  onAcknowledgeGuestWaiver,
  onPrequalificationResponseChange,
  onRefresh,
  onRedeemGuestPass,
  onPlanChangeMembership,
  onPlanChangeTargetChange,
  onPayMembershipBalance,
  onRenewMembership,
  planChangeMembershipTypeId,
  eventRegistrations = [],
  publicPlans = [],
  range,
  changingMembershipPlan,
  payingMembershipBalance = false,
  memberPortalOnly = false,
  renewingMembership,
  reservationEligibility,
  submittingBackgroundCheck = false,
  onStartBackgroundCheck,
  onSubmitPrequalification
}) {
  const copy = currentStatusCopy(statusContext);
  const [membershipSection, setMembershipSection] = useState("plan");
  const defaultedBlockedSectionRef = useRef(false);
  const member = statusContext?.members?.find((item) => item.memberId === statusContext.current?.memberId);
  const intent = statusContext?.signupIntents?.find((item) => item.signupIntentId === statusContext.current?.signupIntentId)
    || statusContext?.signupIntents?.[0];
  const intentPlan = publicPlans.find((plan) => plan.membershipTypeId === intent?.membershipTypeId);
  const requirements = member?.review?.requirements || {};
  const workspace = workspaceNoun(range || statusContext?.range, "range");
  const waiver = requirements.waiver || statusContext?.membership?.reviewPolicy?.publicSignup?.waiver || {};
  const prequalification = requirements.prequalification || statusContext?.membership?.reviewPolicy?.publicSignup?.prequalification || {};
  const backgroundCheck = requirements.backgroundCheck || statusContext?.membership?.reviewPolicy?.publicSignup?.backgroundCheck || {};
  const backgroundCheckCopy = backgroundCheckStatusCopy(backgroundCheck);
  const checkrStatus = String(backgroundCheck.checkr?.status || "").toLowerCase();
  const backgroundCheckCanBeSubmitted = Boolean(
    member &&
    backgroundCheck.required !== false &&
    backgroundCheck.required &&
    backgroundCheck.status !== "completed" &&
    (backgroundCheck.provider === "checkr" || backgroundCheck.checkr) &&
    !["submitted", "in_progress", "ready_for_owner_review"].includes(checkrStatus)
  );
  const [backgroundCheckForm, setBackgroundCheckForm] = useState(() => {
    const nameParts = String(member?.fullName || "").trim().split(/\s+/).filter(Boolean);
    return {
      legalFirstName: nameParts[0] || "",
      legalMiddleName: "",
      legalLastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",
      email: member?.email || "",
      phone: member?.phone || "",
      dateOfBirth: "",
      ssn: "",
      addressLine1: member?.address?.line1 || "",
      addressLine2: member?.address?.line2 || "",
      city: member?.address?.city || "",
      state: member?.address?.state || "",
      postalCode: member?.address?.postalCode || "",
      consentAccepted: false
    };
  });
  const orientationCopy = orientationStatusCopy(prequalification, range || statusContext?.range);
  const orientationAttendance = orientationAttendanceCopy(prequalification, eventRegistrations, range || statusContext?.range);
  const longRangeGateCopy = longRangeGateStatusCopy(member, range || statusContext?.range);
  const accessCardCopy = accessCredentialCopy(member);
  const checklist = prequalification.checklist || [];
  const policyApplicationForm = statusContext?.membership?.reviewPolicy?.publicSignup?.applicationForm;
  const applicationForm = applicationFormIsPublished(prequalification.applicationForm)
    ? normalizeApplicationFormForClient(prequalification.applicationForm)
    : normalizeApplicationFormForClient(policyApplicationForm);
  const applicationResponses = prequalification.applicationResponses || [];
  const applicationSections = applicationFormSectionsWithResponses(applicationForm, applicationResponses);
  const hasApplicationForm = applicationFormIsPublished(applicationForm);
  const applicationSubmitted = Boolean(prequalification.applicationSubmittedAt || applicationResponses.length > 0 || prequalification.status === "completed");
  const waiverCopy = waiverStatusCopy(waiver);
  const checklistSummary = onboardingChecklistSummary(checklist);
  const libertyMilestones = libertyAccessMilestones({
    member,
    prequalification,
    eventRegistrations,
    range: range || statusContext?.range
  });
  const memberWaiverStatus = String(requirements.waiver?.status || waiver.status || "").trim().toLowerCase();
  const policyWaiverRequired = statusContext?.membership?.reviewPolicy?.publicSignup?.waiver?.required !== false;
  const waiverActionNeeded = Boolean(member && (
    waiverRequiresSignature(waiver) ||
    (policyWaiverRequired && !["completed", "accepted", "approved"].includes(memberWaiverStatus))
  ));
  const waiverCanBeAcknowledged = Boolean(member && waiverActionNeeded);
  const prequalificationCanBeSubmitted = member
    && prequalification.required !== false
    && prequalification.status !== "completed"
    && (checklist.length > 0 || hasApplicationForm);
  const canContinueCheckout = !member && intent?.paymentStatus === "checkout_started" && intent.checkout?.url;
  const resolvedMemberPlan = member
    ? member.membershipPlan || publicPlans.find((plan) => plan.membershipTypeId === member.membershipTypeId) || {}
    : null;
  const subscriptionCopy = member ? memberSubscriptionSummary({ ...member, membershipPlan: resolvedMemberPlan }) : null;
  const memberPlanIsLifetime = Boolean(resolvedMemberPlan?.lifetime || resolvedMemberPlan?.term?.unit === "lifetime");
  const canRenewMembership = member
    && ["active", "expired"].includes(member.status)
    && member.membershipPlan?.category !== "day_pass"
    && !memberPlanIsLifetime;
  const planChangeOptions = member
    ? publicPlans.filter((plan) => (
      plan.status !== "inactive" &&
      plan.category !== "day_pass" &&
      plan.membershipTypeId !== member.membershipTypeId
    ))
    : [];
  const selectedPlanChangeOption = planChangeOptions.find((plan) => plan.membershipTypeId === planChangeMembershipTypeId);
  const selectedPlanChangeUnavailable = selectedPlanChangeOption ? planUnavailableMessage(selectedPlanChangeOption) : "";
  const currentPlanCheckoutUnavailable = member?.membershipPlan ? planUnavailableMessage(member.membershipPlan) : "";
  const canStartRenewal = canRenewMembership && !currentPlanCheckoutUnavailable;
  const canStartPlanChange = selectedPlanChangeOption && !selectedPlanChangeUnavailable;
  const canManageMembershipBilling = member
    && ["active", "expired"].includes(member.status)
    && member.membershipPlan?.category !== "day_pass"
    && (canRenewMembership || planChangeOptions.length > 0);
  const guestPassLedger = member?.guestPassLedger || {};
  const guestPassesRemaining = Number(guestPassLedger.remaining || 0);
  const pendingGuestWaivers = (guestPassLedger.entries || [])
    .filter((entry) => entry.action === "use" && entry.guestWaiver?.required !== false && entry.guestWaiver?.status !== "completed");
  const benefitItems = memberPlanBenefitItems(member);
  const qualifications = member?.qualifications?.items || [];
  const missingQualifications = memberPortalOnly ? [] : missingQualificationSummaries(reservationEligibility);
  const activeQualificationCount = qualifications.filter((qualification) => qualification.status === "active").length;
  const pendingQualificationCount = qualifications.filter((qualification) => qualification.status === "pending").length;
  const followUpCopy = memberReviewFollowUpCopy(member);
  const memberPortalReviewTitle = memberPortalOnly && member
    ? waiverActionNeeded
      ? "Release form needed"
      : member.status === "active"
      ? "Membership active"
      : followUpCopy?.title || "Membership in review"
    : memberPortalOnly && !member && !intent
      ? "Membership not connected to this account"
    : copy.title;
  const memberPortalReviewBody = memberPortalOnly && member
    ? waiverActionNeeded
      ? "Finish the current release form before visits stay ready."
      : member.status === "active"
      ? `Your membership is active for this ${workspace}.`
      : followUpCopy?.body || `Your signup is recorded. The ${workspace} team will approve access after any required member steps are complete.`
    : memberPortalOnly && !member && !intent
      ? `We do not see a membership record tied to this signed-in account for this ${workspace}. Start a membership plan or contact the ${workspace} if you joined with another email.`
    : copy.body;
  const visibleError = memberPortalOnly && !member && !intent && /authorization header|required|unauthorized|forbidden|jwt|token/i.test(String(error || ""))
    ? ""
    : memberPortalOnly ? memberPortalFriendlyError(error, workspace) : error;
  const paymentStatusLabel = member && (member.stripe?.paymentStatus || intent?.paymentStatus)
    ? memberPaymentStatusLabel(member.stripe?.paymentStatus || intent?.paymentStatus)
    : "";
  const balanceDueCents = Number(member?.membership?.balanceDueCents || 0);
  const balancePaymentStatus = member?.membership?.balancePaymentStatus || member?.stripe?.balancePaymentStatus || "";
  const balanceDueAt = member?.membership?.balanceDueAt;
  const balanceCheckoutOpen = ["balance_due", "checkout_started"].includes(balancePaymentStatus) && balanceDueCents > 0;
  const balanceCheckoutExpired = balanceDueAt && Date.parse(balanceDueAt) <= Date.now() && balancePaymentStatus !== "paid";
  const canPayMembershipBalance = Boolean(
    member &&
    member.review?.status === "approved" &&
    balanceCheckoutOpen &&
    !balanceCheckoutExpired
  );
  const memberPortalReviewItems = member
    ? [
        {
          label: "Plan",
          value: resolvedMemberPlan?.name || "Selected membership plan",
          detail: resolvedMemberPlan?.pricing?.amountCents
            ? formatMoney(resolvedMemberPlan.pricing)
            : formatTerm(resolvedMemberPlan || {})
        },
        {
          label: "Home chapter",
          value: memberChapterLabel(member) || "Chapter not assigned",
          detail: member.homeChapterId ? "Roster is scoped to this chapter." : "Ask the organization to assign your chapter."
        },
        {
          label: balanceDueCents > 0 ? "Payment" : "Dues",
          value: balanceDueCents > 0
            ? balancePaymentStatus === "paid" ? "Paid" : paymentStatusLabel || "Pending"
            : "Current",
          detail: balanceDueCents > 0
            ? `${formatMoney({ amountCents: balanceDueCents, currency: resolvedMemberPlan?.pricing?.currency || "usd" })} balance${balanceDueAt ? ` due ${formatDateOnly(balanceDueAt)}` : ""}`
            : resolvedMemberPlan?.pricing?.amountCents
              ? `${formatMoney(resolvedMemberPlan.pricing)} annual dues`
              : "No online balance due"
        },
        {
          label: "Membership term",
          value: `${formatDateOnly(member.membership?.currentTermStart, "Starts after approval")} - ${resolvedMemberPlan?.lifetime ? "Lifetime" : formatDateOnly(member.membership?.currentTermEnd, "End not set")}`,
          detail: resolvedMemberPlan?.activation?.label
            ? `Starts with ${resolvedMemberPlan.activation.label.toLowerCase()}`
            : formatTerm(resolvedMemberPlan || {})
        },
        {
          label: "Readiness",
          value: waiverActionNeeded ? "Release form needed" : followUpCopy?.label || memberStatusLabel(member.status),
          detail: waiverActionNeeded
            ? "Sign the current release form before your next visit."
            : member.status === "active" ? "Access is active." : `Waiting for ${workspace} approval`
        }
      ]
    : [];
  const memberPortalNextSteps = [];
  const showApplicationForm = hasApplicationForm && (!memberPortalOnly || membershipSection === "requirements");
  const showOnboardingChecklist = !hasApplicationForm && checklist.length > 0 && (!memberPortalOnly || prequalification.status !== "completed");

  useEffect(() => {
    if (defaultedBlockedSectionRef.current || !memberPortalOnly || !waiverActionNeeded) {
      return;
    }
    defaultedBlockedSectionRef.current = true;
    setMembershipSection("requirements");
  }, [memberPortalOnly, waiverActionNeeded]);

  if (memberPortalOnly && member && waiverActionNeeded) {
    memberPortalNextSteps.push({
      key: "waiver",
      title: "Sign release form",
      body: waiverCopy.detail
    });
  }

  if (memberPortalOnly && member && (checklist.length > 0 || hasApplicationForm) && prequalification.status !== "completed") {
    memberPortalNextSteps.push({
      key: "onboarding",
      title: hasApplicationForm ? "Complete membership application" : isLibertyLongRange(range || statusContext?.range) ? "Complete orientation steps" : "Complete onboarding",
      body: hasApplicationForm ? `${applicationForm.title || "Application"} is ready to submit` : `${checklistSummary.open} open / ${checklistSummary.submitted} submitted`
    });
  } else if (memberPortalOnly && member && isLibertyLongRange(range || statusContext?.range) && member.status !== "active") {
    memberPortalNextSteps.push({
      key: "orientation",
      title: "Orientation scheduling",
      body: orientationAttendance.label === "Scheduled"
        ? orientationAttendance.detail
        : "The range will schedule orientation before full member access."
    });
  }

  if (memberPortalOnly && member && backgroundCheck.required !== false && backgroundCheck.required && backgroundCheck.status !== "completed") {
    memberPortalNextSteps.push({
      key: "background",
      title: "Background check",
      body: backgroundCheckCopy.detail
    });
  }

  if (memberPortalOnly && canPayMembershipBalance) {
    memberPortalNextSteps.push({
      key: "balance",
      title: "Pay remaining balance",
      body: `${formatMoney({ amountCents: balanceDueCents, currency: member.membershipPlan?.pricing?.currency || "usd" })}${balanceDueAt ? ` due ${formatDateOnly(balanceDueAt)}` : ""}`
    });
  }

  function applicationDraft(question = {}) {
    const existing = applicationResponses.find((response) => response.questionId === question.questionId) || {};
    const draft = prequalificationResponses[question.questionId];
    if (draft && typeof draft === "object") {
      return {
        questionId: question.questionId,
        value: draft.value ?? existing.value ?? (question.type === "multi_choice" ? [] : ""),
        otherText: draft.otherText ?? existing.otherText ?? "",
        details: draft.details ?? existing.details ?? ""
      };
    }
    return {
      questionId: question.questionId,
      value: existing.value ?? (question.type === "multi_choice" ? [] : ""),
      otherText: existing.otherText || "",
      details: existing.details || ""
    };
  }

  function updateApplicationDraft(question, patch) {
    const current = applicationDraft(question);
    onPrequalificationResponseChange(question.questionId, { ...current, ...patch });
  }

  function updateBackgroundCheckForm(patch) {
    setBackgroundCheckForm((current) => ({ ...current, ...patch }));
  }

  function submitBackgroundCheck(event) {
    event.preventDefault();
    onStartBackgroundCheck?.(backgroundCheckForm);
  }

  return (
    <section className="public-status-panel" id={id} aria-live="polite">
      <div className="public-status-header">
        <div>
          <div className="public-checkout-status">
            {waiverActionNeeded
              ? "Action needed"
              : member?.status === "active"
              ? (member?.membershipPlan?.category === "day_pass" ? "Day pass active" : "Active")
              : intent?.paymentStatus === "paid" ? "Payment received" : "Status"}
          </div>
          <h2>{memberPortalReviewTitle}</h2>
          <p>{memberPortalReviewBody}</p>
        </div>
        {memberPortalOnly && waiverActionNeeded ? (
          <div className="member-inline-actions is-header">
            <button
              type="button"
              className="public-primary-link"
              onClick={onAcknowledgeWaiver}
              disabled={acknowledgingWaiver}
            >
              {acknowledgingWaiver ? "Saving..." : RELEASE_FORM_ACTION_LABEL}
            </button>
            <button type="button" className="secondary-button" onClick={onRefresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        ) : memberPortalOnly && !member && !intent ? (
          <div className="member-inline-actions is-header">
            <a className="public-primary-link" href="#membership">Start membership</a>
            <a className="secondary-button" href="#member-support">Ask the {workspace}</a>
            <button type="button" className="secondary-button" onClick={onRefresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        ) : (
          <button type="button" className="secondary-button" onClick={onRefresh} disabled={loading}>
            {loading ? "Checking..." : "Check status"}
          </button>
        )}
      </div>

      {visibleError && <div className="notice is-error">{visibleError}</div>}

      {memberPortalOnly && (
        <MemberPortalSectionTabs
          activeSection={membershipSection}
          onSelect={setMembershipSection}
          sections={[
            { id: "plan", label: "Plan", detail: "Term and review" },
            { id: "requirements", label: "Requirements", detail: "Waiver and steps" },
            { id: "billing", label: "Billing", detail: "Renew or change" }
          ]}
        />
      )}

      {member && (!memberPortalOnly || membershipSection === "plan") && (
        <div className={`public-status-grid ${memberPortalOnly ? "public-member-review-grid" : ""}`}>
          {memberPortalOnly ? (
            memberPortalReviewItems.map((item) => (
              <div key={item.label}>
                <strong>{item.label}</strong>
                <span>{item.value}</span>
                {item.detail && <small>{item.detail}</small>}
              </div>
            ))
          ) : (
            <>
              <div>
                <strong>Plan</strong>
                <span>{member.membershipPlan?.name || "Selected membership plan"}</span>
              </div>
              <div>
                <strong>Access state</strong>
                <span>{formatMemberAccessLabel(member)}</span>
              </div>
              <div>
                <strong>Range review</strong>
                <span>{member.review?.status === "approved" ? "Approved" : member.review?.status || "Pending"}</span>
              </div>
              <div>
                <strong>Term</strong>
                <span>{formatTerm(member.membershipPlan || {})}</span>
              </div>
              <div>
                <strong>Membership term</strong>
                <span>
                  {formatDateOnly(member.membership?.currentTermStart, "Starts after approval")}
                  {" - "}
                  {member.membershipPlan?.lifetime ? "Lifetime" : formatDateOnly(member.membership?.currentTermEnd, "End not set")}
                </span>
              </div>
              <div>
                <strong>Renewal</strong>
                <span>
                  {member.membershipPlan?.lifetime
                    ? "No renewal required"
                    : member.membership?.renewalDate
                      ? formatDateOnly(member.membership.renewalDate)
                      : formatBilling(member.membershipPlan || {})}
                </span>
              </div>
              <div>
                <strong>Reservation access</strong>
                <span>{formatReservationAccessLabel(member)}</span>
              </div>
              <div>
                <strong>Event access</strong>
                <span>{member.status === "active" ? "Can register for eligible events" : "Available after activation"}</span>
              </div>
              <div>
                <strong>Guest passes</strong>
                <span>{guestPassesRemaining > 0 ? `${guestPassesRemaining} available` : "None available"}</span>
              </div>
            </>
          )}
        </div>
      )}

      {memberPortalOnly && !member && !intent && membershipSection === "plan" && (
        <>
          <div className="public-status-grid public-member-review-grid">
            <div>
              <strong>Membership</strong>
              <span>Not connected</span>
              <small>Use the same account you used at signup, or start a plan for this {workspace}.</small>
            </div>
            <div>
              <strong>Payment</strong>
              <span>No member balance shown</span>
              <small>Payments and receipts appear after a membership is connected to this account.</small>
            </div>
          <div>
            <strong>Next step</strong>
            <span>Start or ask for help</span>
            <small>Choose a membership plan or contact the {workspace} if your signup should already be here.</small>
            <div className="member-inline-actions">
              <a className="public-primary-link" href="#membership">Start membership</a>
              <a className="secondary-button" href="#member-support">Ask the {workspace}</a>
            </div>
          </div>
        </div>
        </>
      )}

      {memberPortalOnly && !member && intent && membershipSection === "plan" && (
        <div className="public-status-grid public-member-review-grid">
          <div>
            <strong>Plan</strong>
            <span>{intentPlan?.name || "Selected membership plan"}</span>
            <small>{intentPlan?.pricing ? formatMoney(intentPlan.pricing) : "Plan details will appear after the team links your member record."}</small>
          </div>
          <div>
            <strong>Payment</strong>
            <span>{intent?.paymentStatus === "paid" ? "Paid" : memberPaymentStatusLabel(intent?.paymentStatus || "payment_pending")}</span>
            <small>
              {intent?.paymentStatus === "paid"
                ? "Membership payment is recorded."
                : intent?.checkout?.url
                  ? "Checkout is still open or waiting for confirmation."
                  : "The organization will send payment instructions after review."}
            </small>
          </div>
          <div>
            <strong>{isChapterOrganization(range || statusContext?.range) ? "Organization review" : "Range review"}</strong>
            <span>{intent?.paymentStatus === "paid" ? "Waiting for member record" : "Request received"}</span>
            <small>The team can finish review after your signup is connected to a member record.</small>
          </div>
        </div>
      )}

      {member && !memberPortalOnly && benefitItems.length > 0 && (
        <div className="public-requirements-panel public-member-benefits-panel">
          <h3>Plan benefits</h3>
          <ul>
            {benefitItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {member && waiverIsRequired(waiver) && (!memberPortalOnly || membershipSection === "requirements" || waiverActionNeeded) && (
        <div className={`public-requirements-panel public-waiver-panel ${waiverCopy.expired ? "is-attention" : ""}`}>
          <div className="public-waiver-panel-copy">
            <span className="eyebrow">Release form</span>
            <h3>{waiverDocumentName(waiver)}</h3>
            <p>{waiverCopy.detail}</p>
          </div>
          <div className="public-waiver-meta">
            <div>
              <strong>Status</strong>
              <span>{waiverCopy.label}</span>
            </div>
            <div>
              <strong>Effective</strong>
              <span>{formatDateOnly(waiver.document?.effectiveAt, "Effective date not set")}</span>
            </div>
            <div>
              <strong>Re-sign rule</strong>
              <span>{waiverExpirationRule(waiver)}</span>
            </div>
            <div>
              <strong>Your signature</strong>
              <span>{waiverSignatureLabel(waiver)}</span>
            </div>
          </div>
          <div className="public-waiver-actions">
            {waiver.document?.url && (
              <a className="secondary-button" href={waiver.document.url} target="_blank" rel="noreferrer">
                View release form
              </a>
            )}
            {waiverCanBeAcknowledged && !memberPortalOnly && (
              <button
                type="button"
                className="public-primary-link"
                disabled={acknowledgingWaiver}
                onClick={onAcknowledgeWaiver}
              >
                {acknowledgingWaiver ? "Saving..." : waiverCopy.actionLabel}
              </button>
            )}
          </div>
        </div>
      )}

      {member && !memberPortalOnly && (
        <div className="public-requirements-panel public-qualification-panel">
          <div className="public-qualification-panel-header">
            <div>
              <span className="eyebrow">Range qualifications</span>
              <h3>Access credentials</h3>
              <p>
                Qualifications are range-approved access records for restricted bays, long-range areas, and member-only activities.
              </p>
            </div>
            <div className="public-qualification-counts">
              <span>{activeQualificationCount} active</span>
              <span>{pendingQualificationCount} pending</span>
            </div>
          </div>

          {qualifications.length > 0 ? (
            <div className="member-detail-list public-qualification-list">
              {qualifications.map((qualification) => (
                <div key={qualification.qualificationId || qualification.key}>
                  <strong>{qualification.label || qualificationDisplayLabel(qualification.key)}</strong>
                  <span>{qualificationStatusLabel(qualification.status)} / {qualificationDateLabel(qualification)}</span>
                  {qualification.source && (
                    <span>
                      Recorded by {qualification.source === "member_request" ? "member request" : qualificationDisplayLabel(qualification.source)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="public-reference">
              No qualifications are on file yet. Restricted resources will show what the range needs before you request access.
            </p>
          )}

          {missingQualifications.length > 0 && (
            <div className="public-missing-qualification-list">
              <h4>Currently blocking reservations</h4>
              {missingQualifications.map((qualification) => (
                <div key={qualification.key}>
                  <strong>{qualification.label}</strong>
                  <span>
                    Required for {qualification.resources.slice(0, 3).join(", ")}
                    {qualification.resources.length > 3 ? ` and ${qualification.resources.length - 3} more` : ""}
                  </span>
                  <a href="#reservations">Request from Reservations</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {member && !memberPortalOnly && isLibertyLongRange(range || statusContext?.range) && (
        <div className="public-requirements-panel public-orientation-panel">
          <div className="public-qualification-panel-header">
            <div>
              <span className="eyebrow">Liberty access path</span>
              <h3>Orientation and gated range access</h3>
              <p>
                Range orientation is the safety and rules checkpoint. The 1,000-yard area remains a separate optional qualification gate.
              </p>
            </div>
          </div>
          <div className="public-liberty-milestones">
            {libertyMilestones.map((milestone) => (
              <article key={milestone.key}>
                <div>
                  <strong>{milestone.title}</strong>
                  <span>{milestone.required ? "Required" : "Optional"}</span>
                </div>
                <p>{milestone.body}</p>
                <small>{milestone.detail}</small>
                <em>{milestone.label}</em>
              </article>
            ))}
          </div>
          <dl className="public-member-billing-summary">
            <div>
              <dt>Orientation</dt>
              <dd>{orientationAttendance.label}</dd>
            </div>
            <div>
              <dt>Attendance detail</dt>
              <dd>{orientationAttendance.detail}</dd>
            </div>
            <div>
              <dt>1,000-yard gate</dt>
              <dd>{longRangeGateCopy.label}</dd>
            </div>
            <div>
              <dt>Gate detail</dt>
              <dd>{longRangeGateCopy.detail}</dd>
            </div>
            <div>
              <dt>Access card</dt>
              <dd>{accessCardCopy.label}</dd>
            </div>
            <div>
              <dt>Card detail</dt>
              <dd>{accessCardCopy.detail}</dd>
            </div>
          </dl>
        </div>
      )}

      {!memberPortalOnly && member?.status === "active" && guestPassesRemaining > 0 && (
        <div className="public-requirements-panel">
          <h3>Guest pass</h3>
          <p>Redeem one available guest pass for a range visit. Staff can review the ledger from the member record.</p>
          <button type="button" className="secondary-button" onClick={onRedeemGuestPass} disabled={redeemingGuestPass}>
            {redeemingGuestPass ? "Redeeming..." : "Redeem guest pass"}
          </button>
        </div>
      )}

      {!memberPortalOnly && pendingGuestWaivers.length > 0 && (
        <div className="public-requirements-panel">
          <h3>Guest waiver</h3>
          <p>Complete waiver acknowledgement for redeemed guest passes before the visit.</p>
          <div className="member-detail-list">
            {pendingGuestWaivers.map((entry) => (
              <div key={entry.entryId}>
                <strong>{entry.guestName || "Guest"}</strong>
                <span>{entry.guestEmail || "No guest email"} / redeemed {new Date(entry.createdAt).toLocaleDateString()}</span>
                {entry.guestWaiver?.instructions && <span>{entry.guestWaiver.instructions}</span>}
                <button
                  type="button"
                  className="secondary-button"
                  disabled={acknowledgingGuestWaiverEntryId === entry.entryId}
                  onClick={() => onAcknowledgeGuestWaiver(entry.entryId)}
                >
                  {acknowledgingGuestWaiverEntryId === entry.entryId ? "Saving..." : "Acknowledge guest waiver"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {intent && !member && (
        <p className="public-checkout-note">
          {intent.paymentStatus === "paid"
            ? "Your payment has been received. Finish the remaining requirements so the team can approve access."
            : intent.checkout?.url
              ? "Checkout is still open. Finish payment when you are ready, then return here to continue setup."
              : `Your request is waiting for ${workspace} follow-up. Payment instructions will appear here or arrive from the team after review.`}
        </p>
      )}

      {canContinueCheckout && (
        <a className="public-primary-link" href={intent.checkout.url}>
          Continue checkout
        </a>
      )}

      {memberPortalOnly && membershipSection === "billing" && member && balanceDueCents > 0 && (
        <div className="public-requirements-panel public-member-billing-panel">
          <div className="public-member-billing-header">
            <div>
              <span className="eyebrow">Remaining balance</span>
              <h3>{balancePaymentStatus === "paid" ? "Membership balance paid" : "Balance due after approval"}</h3>
              <p>
                {balancePaymentStatus === "paid"
                  ? "The remaining membership balance is paid and your membership can be active."
                  : "Your screening has been approved. Pay the remaining balance before the due date to activate the membership term."}
              </p>
            </div>
            <span>{formatMoney({ amountCents: balanceDueCents, currency: member.membershipPlan?.pricing?.currency || "usd" })}</span>
          </div>
          <dl className="public-member-billing-summary">
            <div>
              <dt>Due date</dt>
              <dd>{balanceDueAt ? formatDateOnly(balanceDueAt) : "Set by range approval"}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{balanceCheckoutExpired ? "Payment window expired" : memberPaymentStatusLabel(balancePaymentStatus || "payment_pending")}</dd>
            </div>
          </dl>
          {canPayMembershipBalance ? (
            <button
              type="button"
              className="public-primary-link"
              disabled={payingMembershipBalance}
              onClick={() => onPayMembershipBalance(member)}
            >
              {payingMembershipBalance ? "Opening checkout..." : "Pay remaining balance"}
            </button>
          ) : balanceCheckoutExpired ? (
            <p className="public-checkout-note">Contact the range to reopen the balance payment window.</p>
          ) : null}
        </div>
      )}

      {canManageMembershipBilling && (!memberPortalOnly || membershipSection === "billing") && (
        <div className="public-requirements-panel public-member-billing-panel">
          <div className="public-member-billing-header">
            <div>
              <span className="eyebrow">Membership billing</span>
              <h3>Renew or change plan</h3>
              <p>
                Use renewal to extend the current plan. Use plan change when you want a different published plan and are ready to start a new checkout.
              </p>
            </div>
            <span>{memberPlanIsLifetime ? "Lifetime" : formatBilling(member.membershipPlan || {})}</span>
          </div>
          <dl className="public-member-billing-summary">
            <div>
              <dt>Current plan</dt>
              <dd>{member.membershipPlan?.name || "Selected membership plan"}</dd>
            </div>
            <div>
              <dt>Billing cadence</dt>
              <dd>{subscriptionCopy?.cadence || formatBilling(member.membershipPlan || {})}</dd>
            </div>
            <div>
              <dt>Subscription</dt>
              <dd>
                {subscriptionCopy
                  ? [subscriptionCopy.statusLabel, subscriptionCopy.cancelLabel].filter(Boolean).join(" / ")
                  : "Range-managed"}
              </dd>
            </div>
            <div>
              <dt>Renewal</dt>
              <dd>
                {subscriptionCopy?.nextBillingLabel || (member.membership?.renewalDate
                  ? `Renews ${formatDateOnly(member.membership.renewalDate)}`
                  : memberPlanIsLifetime
                    ? "No renewal needed"
                    : "Extends current plan")}
              </dd>
            </div>
            <div>
              <dt>Checkout</dt>
              <dd>{currentPlanCheckoutUnavailable || planCheckoutLabel(member.membershipPlan || {}) || "Range-managed checkout"}</dd>
            </div>
          </dl>
          <div className="public-member-billing-actions">
            {canRenewMembership ? (
              <button
                type="button"
                className="public-primary-link"
                disabled={renewingMembership || !canStartRenewal}
                onClick={() => onRenewMembership(member)}
              >
                {renewingMembership ? "Starting renewal..." : "Renew current plan"}
              </button>
            ) : (
              <span>Renewal is not needed for this plan.</span>
            )}
            {canRenewMembership && currentPlanCheckoutUnavailable && <span>{currentPlanCheckoutUnavailable}</span>}
          </div>
          {planChangeOptions.length > 0 && (
            <div className="public-member-plan-change">
              <label>
                Change plan
                <select
                  value={planChangeMembershipTypeId}
                  onChange={(event) => onPlanChangeTargetChange(event.target.value)}
                >
                  <option value="">Select a plan</option>
                  {planChangeOptions.map((plan) => {
                    const unavailable = planUnavailableMessage(plan);
                    return (
                      <option key={plan.membershipTypeId} value={plan.membershipTypeId} disabled={Boolean(unavailable)}>
                        {unavailable ? `${plan.name} - ${unavailable}` : plan.name}
                      </option>
                    );
                  })}
                </select>
              </label>
              {selectedPlanChangeOption ? (
                <dl className="public-member-billing-summary is-plan-change">
                  <div>
                    <dt>Target plan</dt>
                    <dd>{selectedPlanChangeOption.name}</dd>
                  </div>
                  <div>
                    <dt>Price</dt>
                    <dd>{formatMoney(selectedPlanChangeOption.pricing)}</dd>
                  </div>
                  <div>
                    <dt>Term</dt>
                    <dd>{formatTerm(selectedPlanChangeOption)}</dd>
                  </div>
                  <div>
                    <dt>Billing</dt>
                    <dd>{formatBilling(selectedPlanChangeOption)}</dd>
                  </div>
                  <div>
                    <dt>Range review</dt>
                    <dd>
                      The range will apply the new plan after payment is complete.
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="public-reference">Select a published plan to preview the price, term, and billing effect before checkout.</p>
              )}
              {selectedPlanChangeUnavailable && <p className="public-checkout-note">{selectedPlanChangeUnavailable}</p>}
              <button
                type="button"
                className="secondary-button"
                disabled={changingMembershipPlan || !canStartPlanChange}
                onClick={() => onPlanChangeMembership(member)}
              >
                {changingMembershipPlan ? "Starting checkout..." : "Start plan-change checkout"}
              </button>
            </div>
          )}
        </div>
      )}

      {memberPortalOnly && membershipSection === "billing" && member && !(balanceDueCents > 0) && !canManageMembershipBilling && (
        <div className="public-requirements-panel public-member-billing-panel">
          <div className="public-member-billing-header">
            <div>
              <span className="eyebrow">Billing</span>
              <h3>No member action needed</h3>
              <p>Your payment is recorded for the current membership. Any future renewal or plan-change action will appear here when available.</p>
            </div>
            <span>{paymentStatusLabel || "Recorded"}</span>
          </div>
          <dl className="public-member-billing-summary">
            <div>
              <dt>Current plan</dt>
              <dd>{member.membershipPlan?.name || "Selected membership plan"}</dd>
            </div>
            <div>
              <dt>Billing</dt>
              <dd>{subscriptionCopy?.nextBillingLabel || "No current balance due"}</dd>
            </div>
          </dl>
        </div>
      )}

      {memberPortalOnly && membershipSection === "billing" && !member && (
        <div className="public-requirements-panel public-member-billing-panel">
          <div className="public-member-billing-header">
            <div>
              <span className="eyebrow">Billing</span>
              <h3>{intent?.paymentStatus === "paid" ? "Payment recorded" : intent?.checkout?.url ? "Checkout status" : "Payment follow-up"}</h3>
              <p>
                {intent?.paymentStatus === "paid"
                  ? "The membership payment is recorded. The team can complete review after the member record is connected."
                  : intent?.checkout?.url
                    ? "Finish checkout first, then return here for member billing details."
                    : "No online checkout is open for this request. The organization will follow up with payment instructions."}
              </p>
            </div>
            <span>{intent?.paymentStatus === "paid" ? "Paid" : memberPaymentStatusLabel(intent?.paymentStatus || "payment_pending")}</span>
          </div>
        </div>
      )}

      {memberPortalOnly && membershipSection === "requirements" && member && (
        <div className="public-next-steps public-member-next-steps">
          <div>
            <strong>Release form</strong>
            <span>
              {waiverIsRequired(waiver)
                ? `${waiverCopy.label} / ${waiverDocumentName(waiver)}`
                : "No release-form action is needed right now."}
            </span>
          </div>
          <div>
            <strong>{isLibertyLongRange(range || statusContext?.range) ? "Orientation" : "Onboarding"}</strong>
            <span>
              {checklist.length > 0
                ? `${checklistSummary.complete} complete / ${checklistSummary.submitted} submitted / ${checklistSummary.open} open`
                : requirementStatusLabel(prequalification)}
            </span>
            <span>{orientationCopy.detail}</span>
          </div>
          {backgroundCheck.required !== false && backgroundCheck.required && (
            <div>
              <strong>Background check</strong>
              <span>{backgroundCheckCopy.label}</span>
              <span>{backgroundCheckCopy.detail}</span>
            </div>
          )}
          {isLibertyLongRange(range || statusContext?.range) && (
            <>
              <div>
                <strong>1,000-yard gate</strong>
                <span>{longRangeGateCopy.label}</span>
                <span>{longRangeGateCopy.detail}</span>
              </div>
              <div>
                <strong>Access card</strong>
                <span>{accessCardCopy.label}</span>
                <span>{accessCardCopy.detail}</span>
              </div>
            </>
          )}
          <div>
            <strong>Access</strong>
            <span>{member.status === "active" ? "Member access is active." : "Access starts after range approval."}</span>
          </div>
        </div>
      )}

      {memberPortalOnly && membershipSection === "requirements" && memberPortalNextSteps.length > 0 && (
        <div className="public-next-steps public-member-next-steps">
          {memberPortalNextSteps.map((step) => (
            <div key={step.key}>
              <strong>{step.title}</strong>
              <span>{step.body}</span>
            </div>
          ))}
        </div>
      )}

      {!memberPortalOnly && (
        <div className="public-next-steps">
          <div>
            <strong>Release form</strong>
            <span>{waiverCopy.label} / {waiverDocumentName(waiver)}</span>
          </div>
          <div>
            <strong>{isLibertyLongRange(range || statusContext?.range) ? "Orientation" : "Onboarding"}</strong>
            <span>
              {checklist.length > 0
                ? `${checklistSummary.complete} complete / ${checklistSummary.submitted} submitted / ${checklistSummary.open} open`
                : requirementStatusLabel(prequalification)}
            </span>
          </div>
          {backgroundCheck.required !== false && backgroundCheck.required && (
            <div>
              <strong>Background check</strong>
              <span>{backgroundCheckCopy.label}</span>
              <span>{backgroundCheckCopy.detail}</span>
            </div>
          )}
          {member && isLibertyLongRange(range || statusContext?.range) && (
            <>
              <div>
                <strong>Orientation</strong>
                <span>{orientationCopy.label}</span>
                <span>{orientationCopy.detail}</span>
              </div>
              <div>
                <strong>1,000-yard gate</strong>
                <span>{longRangeGateCopy.label}</span>
                <span>{longRangeGateCopy.detail}</span>
              </div>
              <div>
                <strong>Access card</strong>
                <span>{accessCardCopy.label}</span>
                <span>{accessCardCopy.detail}</span>
              </div>
            </>
          )}
          <div>
            <strong>Access</strong>
            <span>{member?.status === "active" ? "Member access is active." : "Access starts after range approval."}</span>
          </div>
        </div>
      )}

      {memberPortalOnly && membershipSection === "requirements" && !member && (
        <div className="public-next-steps public-member-next-steps">
          <div>
            <strong>Member record pending</strong>
            <span>Requirements such as orientation, release forms, gate access, and background-check status appear after the range links this signup to your member record.</span>
          </div>
        </div>
      )}

      {memberPortalOnly && membershipSection === "requirements" && member && backgroundCheck.required !== false && backgroundCheck.required && (
        <form className="public-status-checklist public-background-check-form" onSubmit={submitBackgroundCheck}>
          <div className="public-onboarding-checklist-header">
            <div>
              <span className="eyebrow">Background check</span>
              <h3>{backgroundCheckCopy.label}</h3>
              <p>{backgroundCheckCopy.detail}</p>
            </div>
            <span>{backgroundCheck.checkr?.packageName || backgroundCheck.checkr?.packageId || "Screening"}</span>
          </div>
          {backgroundCheckCanBeSubmitted ? (
            <>
              <div className="public-background-check-grid">
                <label>
                  Legal first name
                  <input
                    value={backgroundCheckForm.legalFirstName}
                    onChange={(event) => updateBackgroundCheckForm({ legalFirstName: event.target.value })}
                    required
                    autoComplete="given-name"
                  />
                </label>
                <label>
                  Middle name
                  <input
                    value={backgroundCheckForm.legalMiddleName}
                    onChange={(event) => updateBackgroundCheckForm({ legalMiddleName: event.target.value })}
                    autoComplete="additional-name"
                  />
                </label>
                <label>
                  Legal last name
                  <input
                    value={backgroundCheckForm.legalLastName}
                    onChange={(event) => updateBackgroundCheckForm({ legalLastName: event.target.value })}
                    required
                    autoComplete="family-name"
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={backgroundCheckForm.email}
                    onChange={(event) => updateBackgroundCheckForm({ email: event.target.value })}
                    required
                    autoComplete="email"
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={backgroundCheckForm.phone}
                    onChange={(event) => updateBackgroundCheckForm({ phone: event.target.value })}
                    autoComplete="tel"
                  />
                </label>
                <label>
                  Date of birth
                  <input
                    type="date"
                    value={backgroundCheckForm.dateOfBirth}
                    onChange={(event) => updateBackgroundCheckForm({ dateOfBirth: event.target.value })}
                    required
                    autoComplete="bday"
                  />
                </label>
                <label>
                  SSN
                  <input
                    type="password"
                    value={backgroundCheckForm.ssn}
                    onChange={(event) => updateBackgroundCheckForm({ ssn: event.target.value })}
                    required
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="Required by screening provider"
                  />
                </label>
                <label>
                  Address
                  <input
                    value={backgroundCheckForm.addressLine1}
                    onChange={(event) => updateBackgroundCheckForm({ addressLine1: event.target.value })}
                    autoComplete="address-line1"
                  />
                </label>
                <label>
                  City
                  <input
                    value={backgroundCheckForm.city}
                    onChange={(event) => updateBackgroundCheckForm({ city: event.target.value })}
                    autoComplete="address-level2"
                  />
                </label>
                <label>
                  State
                  <input
                    value={backgroundCheckForm.state}
                    onChange={(event) => updateBackgroundCheckForm({ state: event.target.value })}
                    autoComplete="address-level1"
                  />
                </label>
                <label>
                  ZIP code
                  <input
                    value={backgroundCheckForm.postalCode}
                    onChange={(event) => updateBackgroundCheckForm({ postalCode: event.target.value })}
                    required
                    autoComplete="postal-code"
                  />
                </label>
              </div>
              <label className="public-background-check-consent">
                <input
                  type="checkbox"
                  checked={backgroundCheckForm.consentAccepted}
                  onChange={(event) => updateBackgroundCheckForm({ consentAccepted: event.target.checked })}
                  required
                />
                <span>I authorize the range to run a membership background check through its screening provider.</span>
              </label>
              <button type="submit" className="secondary-button" disabled={submittingBackgroundCheck}>
                {submittingBackgroundCheck ? "Submitting..." : "Submit background check"}
              </button>
            </>
          ) : (
            <div className="public-background-check-state">
              <strong>{backgroundCheckCopy.label}</strong>
              <span>{backgroundCheckCopy.detail}</span>
            </div>
          )}
        </form>
      )}

      {showApplicationForm && (
        <form className="public-status-checklist public-onboarding-checklist public-application-form" onSubmit={onSubmitPrequalification}>
          <div className="public-onboarding-checklist-header">
            <div>
              <span className="eyebrow">Membership application</span>
              <h3>{applicationForm.title || "Membership application"}</h3>
              {applicationForm.description && <p>{applicationForm.description}</p>}
            </div>
            <span>{applicationSubmitted ? "Submitted" : "Ready"}</span>
          </div>
          <div className="public-application-sections">
            {applicationSections.map((section) => (
              <section className="public-application-section" key={section.sectionId}>
                <div className="public-application-section-heading">
                  <strong>{section.title}</strong>
                  {section.description && <span>{section.description}</span>}
                </div>
                {(section.questions || []).map((question) => {
                  const draft = applicationDraft(question);
                  const selectedValues = Array.isArray(draft.value) ? draft.value : [];
                  const isReadOnly = !prequalificationCanBeSubmitted;
                  const questionInputId = `member-application-${question.questionId}`;
                  const questionLabelId = `${questionInputId}-label`;
                  return (
                    <div className="public-application-question" key={question.questionId}>
                      <div className="public-application-question-label">
                        <strong id={questionLabelId}>{question.label}{question.required !== false ? " *" : ""}</strong>
                        {question.helpText && <span>{question.helpText}</span>}
                      </div>
                      {isReadOnly ? (
                        <p>{applicationAnswerDisplay(question.response || draft) || "No response submitted."}</p>
                      ) : question.type === "long_text" ? (
                        <textarea
                          id={questionInputId}
                          aria-labelledby={questionLabelId}
                          value={draft.value || ""}
                          onChange={(event) => updateApplicationDraft(question, { value: event.target.value })}
                          required={question.required !== false}
                          rows="4"
                        />
                      ) : question.type === "short_text" ? (
                        <input
                          id={questionInputId}
                          aria-labelledby={questionLabelId}
                          value={draft.value || ""}
                          onChange={(event) => updateApplicationDraft(question, { value: event.target.value })}
                          required={question.required !== false}
                        />
                      ) : question.type === "yes_no" ? (
                        <div className="public-application-choice-grid">
                          {["yes", "no"].map((value) => (
                            <label className="public-application-choice" key={value}>
                              <input
                                type="radio"
                                name={question.questionId}
                                checked={draft.value === value}
                                onChange={() => updateApplicationDraft(question, { value })}
                                required={question.required !== false}
                              />
                              <span>{value === "yes" ? "Yes" : "No"}</span>
                            </label>
                          ))}
                        </div>
                      ) : question.type === "single_choice" ? (
                        <div className="public-application-choice-grid">
                          {(question.options || []).map((option) => (
                            <label className="public-application-choice" key={option.optionId}>
                              <input
                                type="radio"
                                name={question.questionId}
                                checked={draft.value === option.optionId}
                                onChange={() => updateApplicationDraft(question, { value: option.optionId })}
                                required={question.required !== false}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="public-application-choice-grid">
                          {(question.options || []).map((option) => {
                            const checked = selectedValues.includes(option.optionId);
                            const atLimit = question.maxSelections > 0 && selectedValues.length >= question.maxSelections && !checked;
                            return (
                              <label className="public-application-choice" key={option.optionId}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={atLimit}
                                  onChange={(event) => {
                                    const nextValue = event.target.checked
                                      ? [...selectedValues, option.optionId]
                                      : selectedValues.filter((value) => value !== option.optionId);
                                    updateApplicationDraft(question, { value: nextValue });
                                  }}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                          {question.allowOther && (
                            <label className="public-application-choice">
                              <input
                                type="checkbox"
                                checked={selectedValues.includes("other")}
                                onChange={(event) => {
                                  const nextValue = event.target.checked
                                    ? [...selectedValues, "other"]
                                    : selectedValues.filter((value) => value !== "other");
                                  updateApplicationDraft(question, { value: nextValue });
                                }}
                              />
                              <span>Other</span>
                            </label>
                          )}
                          {question.maxSelections > 0 && (
                            <small>{selectedValues.length}/{question.maxSelections} selected</small>
                          )}
                        </div>
                      )}
                      {!isReadOnly && question.allowOther && selectedValues.includes("other") && (
                        <input
                          aria-label={`Other response for ${question.label}`}
                          value={draft.otherText || ""}
                          onChange={(event) => updateApplicationDraft(question, { otherText: event.target.value })}
                          placeholder="Please specify"
                          required
                        />
                      )}
                      {!isReadOnly && question.conditionalDetails?.enabled && draft.value === question.conditionalDetails.whenValue && (
                        <textarea
                          aria-label={question.conditionalDetails.label || `Details for ${question.label}`}
                          value={draft.details || ""}
                          onChange={(event) => updateApplicationDraft(question, { details: event.target.value })}
                          placeholder={question.conditionalDetails.label || "Please provide details"}
                          required={question.conditionalDetails.required}
                          rows="3"
                        />
                      )}
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
          {prequalificationCanBeSubmitted && (
            <button type="submit" className="secondary-button" disabled={submittingPrequalification}>
              {submittingPrequalification ? "Submitting..." : "Submit membership application"}
            </button>
          )}
        </form>
      )}

      {showOnboardingChecklist && (!memberPortalOnly || membershipSection === "requirements") && (
        <form className="public-status-checklist public-onboarding-checklist" onSubmit={onSubmitPrequalification}>
          <div className="public-onboarding-checklist-header">
            <div>
              <span className="eyebrow">{isLibertyLongRange(range || statusContext?.range) ? "Orientation path" : "Onboarding checklist"}</span>
              <h3>{isLibertyLongRange(range || statusContext?.range) ? "Range orientation and qualification steps" : "Member onboarding steps"}</h3>
              <p>{orientationCopy.detail}</p>
            </div>
            <span>{checklistSummary.complete} complete / {checklistSummary.submitted} submitted / {checklistSummary.open} open</span>
          </div>
          <div className="public-onboarding-steps">
            {checklist.map((step, index) => {
              const stepKey = step.key || `step_${index + 1}`;
              const stepCopy = onboardingStepStatusCopy(step, prequalificationCanBeSubmitted);
              const responseValue = prequalificationResponses[stepKey] || step.applicantResponse || "";
              return (
                <article className={`public-onboarding-step is-${String(stepCopy.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={stepKey}>
                  <div className="public-onboarding-step-copy">
                    <div className="public-onboarding-step-title">
                      <strong>{step.label || `Step ${index + 1}`}</strong>
                      <span>{step.required === false ? "Optional" : "Required"}</span>
                    </div>
                    <p>{onboardingStepPrompt(step)}</p>
                    <small>{stepCopy.detail}</small>
                  </div>
                  <div className="public-onboarding-step-response">
                    <span>{stepCopy.label}</span>
                    {prequalificationCanBeSubmitted ? (
                      <label>
                        <span>Member response</span>
                        <textarea
                          value={responseValue}
                          onChange={(event) => onPrequalificationResponseChange(stepKey, event.target.value)}
                          placeholder="Add your response for the range review team."
                          required={step.required !== false}
                        />
                      </label>
                    ) : (
                      <p>{responseValue || "No member response submitted yet."}</p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
          {prequalificationCanBeSubmitted && (
            <button type="submit" className="secondary-button" disabled={submittingPrequalification}>
              {submittingPrequalification ? "Submitting..." : "Submit onboarding responses"}
            </button>
          )}
        </form>
      )}
    </section>
  );
}
